/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, FormText, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getLabTestItems } from 'app/entities/lab-test-item/lab-test-item.reducer';
import { getEntities as getDiagLabs } from 'app/entities/diag-lab/diag-lab.reducer';
import { Gender } from 'app/shared/model/enumerations/gender.model';
import { createEntity, getEntity, reset, updateEntity } from './lab-item-measure.reducer';

export const LabItemMeasureUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const labTestItems = useAppSelector(state => state.labTestItem.entities);
  const diagLabs = useAppSelector(state => state.diagLab.entities);
  const labItemMeasureEntity = useAppSelector(state => state.labItemMeasure.entity);
  const loading = useAppSelector(state => state.labItemMeasure.loading);
  const updating = useAppSelector(state => state.labItemMeasure.updating);
  const updateSuccess = useAppSelector(state => state.labItemMeasure.updateSuccess);
  const genderValues = Object.keys(Gender);

  const handleClose = () => {
    navigate(`/lab-item-measure${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getLabTestItems({}));
    dispatch(getDiagLabs({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.ageMin !== undefined && typeof values.ageMin !== 'number') {
      values.ageMin = Number(values.ageMin);
    }
    if (values.ageMax !== undefined && typeof values.ageMax !== 'number') {
      values.ageMax = Number(values.ageMax);
    }
    if (values.weightMin !== undefined && typeof values.weightMin !== 'number') {
      values.weightMin = Number(values.weightMin);
    }
    if (values.weightMax !== undefined && typeof values.weightMax !== 'number') {
      values.weightMax = Number(values.weightMax);
    }
    if (values.hiAbsolute !== undefined && typeof values.hiAbsolute !== 'number') {
      values.hiAbsolute = Number(values.hiAbsolute);
    }
    if (values.hiCritical !== undefined && typeof values.hiCritical !== 'number') {
      values.hiCritical = Number(values.hiCritical);
    }
    if (values.hiNormal !== undefined && typeof values.hiNormal !== 'number') {
      values.hiNormal = Number(values.hiNormal);
    }
    if (values.lowAbsolute !== undefined && typeof values.lowAbsolute !== 'number') {
      values.lowAbsolute = Number(values.lowAbsolute);
    }
    if (values.lowCritical !== undefined && typeof values.lowCritical !== 'number') {
      values.lowCritical = Number(values.lowCritical);
    }
    if (values.lowNormal !== undefined && typeof values.lowNormal !== 'number') {
      values.lowNormal = Number(values.lowNormal);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    values.created = convertDateTimeToServer(values.created);
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampentry !== undefined && typeof values.timestampentry !== 'number') {
      values.timestampentry = Number(values.timestampentry);
    }
    if (values.lastupdated !== undefined && typeof values.lastupdated !== 'number') {
      values.lastupdated = Number(values.lastupdated);
    }

    const entity = {
      ...labItemMeasureEntity,
      ...values,
      testItem: labTestItems.find(it => it.id.toString() === values.testItem?.toString()),
      diagLab: diagLabs.find(it => it.id.toString() === values.diagLab?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          created: displayDefaultDateTime(),
        }
      : {
          gender: 'NA',
          ...labItemMeasureEntity,
          created: convertDateTimeFromServer(labItemMeasureEntity.created),
          testItem: labItemMeasureEntity?.testItem?.id,
          diagLab: labItemMeasureEntity?.diagLab?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.labItemMeasure.home.createOrEditLabel" data-cy="LabItemMeasureCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.labItemMeasure.home.createOrEditLabel">Create or edit a LabItemMeasure</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="lab-item-measure-id"
                  label={translate('tinyMedicRestApp.labItemMeasure.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.ageMin')}
                id="lab-item-measure-ageMin"
                name="ageMin"
                data-cy="ageMin"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.ageMax')}
                id="lab-item-measure-ageMax"
                name="ageMax"
                data-cy="ageMax"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.gender')}
                id="lab-item-measure-gender"
                name="gender"
                data-cy="gender"
                type="select"
              >
                {genderValues.map(gender => (
                  <option value={gender} key={gender}>
                    {translate(`tinyMedicRestApp.Gender.${gender}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.weightMin')}
                id="lab-item-measure-weightMin"
                name="weightMin"
                data-cy="weightMin"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.weightMax')}
                id="lab-item-measure-weightMax"
                name="weightMax"
                data-cy="weightMax"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.displayPrecision')}
                id="lab-item-measure-displayPrecision"
                name="displayPrecision"
                data-cy="displayPrecision"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.hiAbsolute')}
                id="lab-item-measure-hiAbsolute"
                name="hiAbsolute"
                data-cy="hiAbsolute"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.hiCritical')}
                id="lab-item-measure-hiCritical"
                name="hiCritical"
                data-cy="hiCritical"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.hiNormal')}
                id="lab-item-measure-hiNormal"
                name="hiNormal"
                data-cy="hiNormal"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.lowAbsolute')}
                id="lab-item-measure-lowAbsolute"
                name="lowAbsolute"
                data-cy="lowAbsolute"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.lowCritical')}
                id="lab-item-measure-lowCritical"
                name="lowCritical"
                data-cy="lowCritical"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.lowNormal')}
                id="lab-item-measure-lowNormal"
                name="lowNormal"
                data-cy="lowNormal"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.units')}
                id="lab-item-measure-units"
                name="units"
                data-cy="units"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.cursor')}
                id="lab-item-measure-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.active')}
                id="lab-item-measure-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.created')}
                id="lab-item-measure-created"
                name="created"
                data-cy="created"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.microSec')}
                id="lab-item-measure-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.timestampentry')}
                id="lab-item-measure-timestampentry"
                name="timestampentry"
                data-cy="timestampentry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.labItemMeasure.lastupdated')}
                id="lab-item-measure-lastupdated"
                name="lastupdated"
                data-cy="lastupdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                id="lab-item-measure-testItem"
                name="testItem"
                data-cy="testItem"
                label={translate('tinyMedicRestApp.labItemMeasure.testItem')}
                type="select"
                required
              >
                <option value="" key="0" />
                {labTestItems
                  ? labTestItems.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="lab-item-measure-diagLab"
                name="diagLab"
                data-cy="diagLab"
                label={translate('tinyMedicRestApp.labItemMeasure.diagLab')}
                type="select"
              >
                <option value="" key="0" />
                {diagLabs
                  ? diagLabs.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/lab-item-measure" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default LabItemMeasureUpdate;
